export const queryKeys = {
  auth: {
    me: ['auth', 'me'] as const,
  },
  problems: {
    all: ['problems'] as const,
    list: (filter: Record<string, unknown>) => [...queryKeys.problems.all, 'list', filter] as const,
    detail: (slug: string) => [...queryKeys.problems.all, 'detail', slug] as const,
    testCases: (slug: string) => [...queryKeys.problems.all, 'testcases', slug] as const,
    editorial: (slug: string) => [...queryKeys.problems.all, 'editorial', slug] as const,
    editorialAccess: (slug: string) =>
      [...queryKeys.problems.all, 'editorial-access', slug] as const,
    stats: () => [...queryKeys.problems.all, 'stats'] as const,
  },
  submissions: {
    all: ['submissions'] as const,
    byProblem: (slug: string) => [...queryKeys.submissions.all, 'problem', slug] as const,
    detail: (id: string) => [...queryKeys.submissions.all, 'detail', id] as const,
    runtimeDistribution: (slug: string) =>
      [...queryKeys.submissions.all, 'runtime-dist', slug] as const,
  },
  users: {
    profile: (username: string) => ['users', 'profile', username] as const,
    stats: (username: string) => ['users', 'stats', username] as const,
  },
  dashboard: {
    stats: (role: string) => ['dashboard', 'stats', role] as const,
  },
  curriculum: {
    all: ['curriculum'] as const,
    templates: () => [...queryKeys.curriculum.all, 'templates'] as const,
    template: (id: string) => [...queryKeys.curriculum.all, 'template', id] as const,
  },
  lms: {
    all: ['lms'] as const,
    myBatches: () => ['lms', 'batches'] as const,
    batchCurriculum: (batchId: string) => ['lms', 'curriculum', batchId] as const,
    topicDetail: (topicId: string) => ['lms', 'topic', topicId] as const,
    leaderboard: (batchId: string) => ['lms', 'leaderboard', batchId] as const,
    upcoming: (batchId: string) => ['lms', 'upcoming', batchId] as const,
    schedule: (batchId: string, from: string, to: string) =>
      ['lms', 'schedule', batchId, from, to] as const,
    recordings: (batchId: string) => ['lms', 'recordings', batchId] as const,
    practice: (batchId: string) => ['lms', 'practice', batchId] as const,
    practiceAssessment: (assessmentId: string) => ['lms', 'practice-assessment', assessmentId] as const,
    home: (batchId: string) => ['lms', 'home', batchId] as const,
    readinessFormula: () => ['lms', 'readiness', 'formula'] as const,
    dailyProblem: () => ['lms', 'daily-problem'] as const,
    hackathons: (batchId: string) => ['lms', 'hackathons', batchId] as const,
    hackathonDetail: (batchId: string, hackathonId: string) =>
      ['lms', 'hackathons', batchId, hackathonId] as const,
    hackathonComponents: (batchId: string, hackathonId: string) =>
      ['lms', 'hackathons', batchId, hackathonId, 'components'] as const,
    hackathonLeaderboard: (batchId: string, hackathonId: string) =>
      ['lms', 'hackathons', batchId, hackathonId, 'leaderboard'] as const,
    hackathonMyTeam: (batchId: string, hackathonId: string) =>
      ['lms', 'hackathons', batchId, hackathonId, 'my-team'] as const,
    hackathonProjectSubmissions: (batchId: string, hackathonId: string, componentId: string) =>
      ['lms', 'hackathons', batchId, hackathonId, 'components', componentId, 'submissions'] as const,
    profileSummary: () => [...queryKeys.lms.all, 'profile-summary'] as const,
    notifications: {
      all: ['lms', 'notifications'] as const,
      list: () => [...queryKeys.lms.notifications.all, 'list'] as const,
      unreadCount: () => [...queryKeys.lms.notifications.all, 'unread-count'] as const,
    },
  },
  trainer: {
    all: ['trainer'] as const,
    batches: () => ['trainer', 'batches'] as const,
    batchOverview: (batchId: string) => ['trainer', 'batch', batchId, 'overview'] as const,
    batchAnnouncements: (batchId: string) => ['trainer', 'batch', batchId, 'announcements'] as const,
    batchCurriculum: (batchId: string) => ['trainer', 'batch', batchId, 'curriculum'] as const,
    dashboard: () => ['trainer', 'dashboard'] as const,
    batchHackathons: (batchId: string) => ['trainer', 'batch', batchId, 'hackathons'] as const,
    hackathonDetail: (batchId: string, hackathonId: string) =>
      ['trainer', 'batch', batchId, 'hackathons', hackathonId] as const,
    hackathonComponents: (batchId: string, hackathonId: string) =>
      ['trainer', 'batch', batchId, 'hackathons', hackathonId, 'components'] as const,
    hackathonTeams: (batchId: string, hackathonId: string) =>
      ['trainer', 'batch', batchId, 'hackathons', hackathonId, 'teams'] as const,
    hackathonLeaderboard: (batchId: string, hackathonId: string) =>
      ['trainer', 'batch', batchId, 'hackathons', hackathonId, 'leaderboard'] as const,
  },
  trainerMessages: {
    all: ['trainer-messages'] as const,
    thread: (batchId: string, trainerId: string) =>
      ['trainer-messages', batchId, trainerId] as const,
  },
  org: {
    all: ['org'] as const,
    overview: () => ['org', 'overview'] as const,
    insights: () => ['org', 'insights'] as const,
    trends: () => ['org', 'trends'] as const,
    batches: () => ['org', 'batches'] as const,
    batchDetail: (batchId: string) => ['org', 'batch', batchId] as const,
    studentDetail: (batchId: string, userId: string) => ['org', 'batch', batchId, 'student', userId] as const,
    studentReadinessEvidence: (batchId: string, userId: string) =>
      ['org', 'batch', batchId, 'student', userId, 'readiness-evidence'] as const,
    // The filters are part of the key: a search is a different result set, not
    // a different page of the same one.
    studentReadinessComponentEvidence: (
      batchId: string,
      userId: string,
      component: string,
      status: string,
      q: string,
    ) =>
      [
        'org', 'batch', batchId, 'student', userId,
        'readiness-evidence', component, status, q,
      ] as const,
    // The review cycle: what changed for a batch since the previous period.
    batchReview: (batchId: string) => ['org', 'batch', batchId, 'review'] as const,
    studentReadinessHistory: (batchId: string, userId: string) =>
      ['org', 'batch', batchId, 'student', userId, 'readiness-history'] as const,
    batchAssignments: (batchId: string) => ['org', 'batch', batchId, 'assignments'] as const,
    assignmentDetail: (batchId: string, assignmentId: string) => ['org', 'batch', batchId, 'assignments', assignmentId] as const,
    batchAssessments: (batchId: string) => ['org', 'batch', batchId, 'assessments'] as const,
    assessmentDetail: (batchId: string, assessmentId: string) => ['org', 'batch', batchId, 'assessments', assessmentId] as const,
    batchSessions: (batchId: string) => ['org', 'batch', batchId, 'sessions'] as const,
    sessionDetail: (batchId: string, sessionId: string) => ['org', 'batch', batchId, 'sessions', sessionId] as const,
    students: () => ['org', 'students'] as const,
    batchHackathons: (batchId: string) => ['org', 'batch', batchId, 'hackathons'] as const,
    hackathonLeaderboard: (batchId: string, hackathonId: string) =>
      ['org', 'batch', batchId, 'hackathons', hackathonId, 'leaderboard'] as const,
    companyReadinessCompanies: () => ['org', 'company-readiness', 'companies'] as const,
    companyReadiness: (company: string, batchId: string) =>
      ['org', 'company-readiness', company, batchId] as const,
    studentCompanyFit: (userId: string) => ['org', 'company-readiness', 'student', userId] as const,
  },
  feedback: {
    all: ['feedback'] as const,
    list: () => ['feedback', 'list'] as const,
  },
  achievements: {
    all: ['achievements'] as const,
    list: () => [...queryKeys.achievements.all, 'list'] as const,
  },
} as const;
